import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError, map, firstValueFrom, from } from 'rxjs';

import { LoggingService } from '../../logging/logging.service';
import { ApiService } from '../../api/api.service';
import { ChangedItemNotification, ChangedItemNotificationsResponse } from '../models/cache-models';
import { LocalStorageService } from '../../local-storage/local-storage.service';

import { CacheManagerService } from './cache-manager.service';

/**
 * Service for handling cache revalidation on startup and app updates.
 * Simplified approach: removes changed items from cache and lets natural cache misses trigger re-fetching.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheRevalidationService {
  private static readonly REVALIDATION_THRESHOLD = 10000; // 10 seconds

  private readonly logger = inject(LoggingService);
  private readonly apiService = inject(ApiService);
  private readonly cacheManager = inject(CacheManagerService);
  private readonly localStorageService = inject(LocalStorageService);

  // Map to store when each store+project was last revalidated
  private lastRevalidationTimestamps = new Map<string, number>();

  /**
   * Revalidate a specific store by removing changed items from cache.
   * Re-fetching will happen naturally when items are accessed and not found in cache.
   *
   * @param storeName Name of the store to revalidate.
   * @param projectId Project ID to scope the revalidation.
   *
   * @returns Promise that resolves when revalidation is complete.
   */
  public async revalidateStore(storeName: string, projectId: string): Promise<void> {
    const cacheKey = `${storeName}_${projectId}`;
    const now = Date.now();

    // Update the timestamp
    this.lastRevalidationTimestamps.set(cacheKey, now);

    // Get the store from the manager
    const store = this.cacheManager.getStore(storeName);

    if (!store) {
      this.logger.warn(`Cannot revalidate store ${storeName} - not registered`);
      return;
    }

    // Fetch lightweight notifications from server
    const notifications = await this.fetchRevalidateNotifications(storeName, projectId);

    // Exit early if no items need revalidation
    if (!notifications.items.length) {
      this.logger.debug(`No items to revalidate for store "${storeName}" in project "${projectId}"`);
      return;
    }

    // Remove all changed items from cache (both deletes and updates)
    // Natural cache misses will trigger re-fetching when items are actually needed
    await this.removeChangedItemsFromCache(storeName, notifications.items);

    // Update the last update timestamp
    const lastUpdateKey = `${storeName}_last_update_${projectId}`;

    this.localStorageService.set(lastUpdateKey, notifications.timestamp.toString());

    this.logger.debug(`Revalidation complete for store "${storeName}" in project "${projectId}". Removed ${notifications.items.length} changed items from cache.`);
  }

  /**
   * Remove changed items from cache store.
   * This applies to both delete and update operations - we simply remove them from cache
   * and let natural access patterns trigger re-fetching when needed.
   *
   * @param storeName The name of the cache store to process.
   * @param changedItems Array of changed item notifications to remove from cache.
   */
  private async removeChangedItemsFromCache(storeName: string, changedItems: ChangedItemNotification[]): Promise<void> {
    if (changedItems.length === 0) {
      return;
    }

    const store = this.cacheManager.getStore(storeName);

    if (!store) {
      return;
    }

    this.logger.debug(`Removing ${changedItems.length} changed items from cache for store "${storeName}"`);

    // Remove all changed items from cache
    for (const item of changedItems) {
      try {
        await firstValueFrom(store.remove(item.id).pipe(
          catchError((error) => {
            this.logger.error(`Error removing item ${item.id} from ${storeName}:`, error);
            return of(false);
          })
        ));
      } catch (error) {
        this.logger.error(`Error removing item ${item.id} from ${storeName}:`, error);
      }
    }
  }

  /**
   * Get lightweight notifications of items that have changed since a specified timestamp.
   *
   * @param storeName Name of the store to check.
   * @param projectId Project ID to scope the request.
   *
   * @returns Promise of changed item notifications.
   */
  private async fetchRevalidateNotifications(storeName: string, projectId: string): Promise<ChangedItemNotificationsResponse> {
    // Get the last update timestamp from local storage
    const lastUpdateKey = `${storeName}_last_update_${projectId}`;
    const lastUpdate = this.localStorageService.get(lastUpdateKey);
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;

    const now = Date.now();

    // Only re-validate if we have not checked recently.
    // Default threshold is 5 seconds.
    if (lastUpdateTimestamp >= (now - CacheRevalidationService.REVALIDATION_THRESHOLD)) {
      return { items: [], timestamp: Date.now() };
    }

    // Fetch lightweight notifications (IDs only) from API
    const apiUrl = `projects/${projectId}/${storeName}/changed-since`;

    try {
      return await firstValueFrom(this.apiService.get<ChangedItemNotificationsResponse>(apiUrl, undefined, {
        timestamp: lastUpdateTimestamp,
        'ngsw-bypass': true
      }).pipe(
        catchError(error => {
          this.logger.error(`Error fetching changed item notifications for ${storeName}:`, error);
          return of({ items: [], timestamp: Date.now() });
        })
      ));
    } catch (error) {
      this.logger.error(`Error fetching changed item notifications for ${storeName}:`, error);
      return { items: [], timestamp: Date.now() };
    }
  }

  /**
   * Check if a specific item needs invalidation.
   * Used by cacheOrFetch operator for individual item checks.
   *
   * @param storeName Name of the store to check.
   * @param projectId Project ID to scope the request.
   * @param itemId ID of the specific item to check.
   *
   * @returns Promise<boolean> indicating if the item needs invalidation.
   */
  public async checkItemNeedsInvalidation(storeName: string, projectId: string, itemId: string | number): Promise<boolean> {
    const cacheKey = `${storeName}_${projectId}`;
    const now = Date.now();
    const lastCheck = this.lastRevalidationTimestamps.get(cacheKey) || 0;

    // Only check if we haven't checked recently (10-second threshold)
    if (now - lastCheck < CacheRevalidationService.REVALIDATION_THRESHOLD) {
      return false; // Recently checked, assume no invalidation needed
    }

    // Update the timestamp to prevent excessive checks
    this.lastRevalidationTimestamps.set(cacheKey, now);

    // Get the last update timestamp from local storage
    const lastUpdateKey = `${storeName}_last_update_${projectId}`;
    const lastUpdate = this.localStorageService.get(lastUpdateKey);
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;

    try {
      // Use DataApiService to check for invalidation (now requires timestamp parameter)
      const needsInvalidation = await firstValueFrom(this.apiService.get<ChangedItemNotificationsResponse>(`projects/${projectId}/${storeName}/changed-since`, undefined, {
        timestamp: lastUpdateTimestamp,
        itemId: itemId,
        'ngsw-bypass': true
      }).pipe(
        map(response => {
          return response?.items?.some(item => item.id === itemId);
        }),
        catchError(error => {
          this.logger.error(`Error checking item ${itemId} invalidation:`, error);
          return of(false);
        })
      ));

      return needsInvalidation || false;
    } catch (error) {
      this.logger.error(`Error checking item ${itemId} invalidation:`, error);
      return false;
    }
  }

  /**
   * Revalidate all registered stores.
   *
   * @param projectId The project ID to scope the revalidation.
   *
   * @returns Observable that completes when all revalidations are done.
   */
  public revalidateAll(projectId: string): Observable<boolean> {
    const stores = this.cacheManager.getAllStores();

    if (stores.length === 0) {
      return of(true);
    }

    const promises = stores.map(storeName => {
      const lastUpdateKey = `${storeName}_last_update_${projectId}`;

      this.localStorageService.set(lastUpdateKey, '0');
      return this.revalidateStore(storeName, projectId);
    });

    return from(Promise.all(promises)).pipe(
      map(() => true),
      catchError(error => {
        this.logger.error('Error revalidating all stores:', error);
        return of(false);
      })
    );
  }
}
